import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Chat, LanguageCode, Profile, validateVideo, Video } from "../../models";
import { createVideo, getChats, getLanguageCodes, getVideo, updateVideo } from "../../api";
import { getProfiles } from "../../api/profiles";
import Header from "../../header/header";
import { Button, LinearProgress } from "@mui/material";
import { VideoForm } from "../components/VideoForm";

export const VideoDetailPage: React.FC = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Data state
  const [video, setVideo] = useState<Omit<Video, "id" | "createdAt" | "updatedAt"> | null>(null);
  const [languageCodes, setLanguageCodes] = useState<LanguageCode[] | null>(null);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [chats, setChats] = useState<Chat[] | null>(null);
  const [error, setError] = useState<any | null>(null);

  // Form
  const [videoErrors, setVideoErrors] = useState<Record<keyof Video, string>>();
  const [dirty, setDirty] = useState(false);

  const loading = useMemo(() => !(video && languageCodes && profiles) && !error, [video, languageCodes, profiles, error]);

  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setError(null);

    try {
      if (id === "new") {
        const [languageCodes, profiles, chats] = await Promise.all([
          await getLanguageCodes(),
          await getProfiles(),
          await getChats(),
        ]);

        setVideo({
          name: "",
          title: "",
          description: "",
          thumbnailUrl: "",
          contentUrl: "",
          duration: 0,
          publishedAt: "",
          viewCount: 0,
          likeCount: 0,
          dislikeCount: 0,
          commentCount: 0,
          muted: false,
          viewabilityTracking: true,
          processed: false,
          status: "active",
          profileId: 1,
          languageCodeId: 1,
        });
        setLanguageCodes(languageCodes);
        setProfiles(profiles);
        setChats(chats);
      } else {
        const [video, languageCodes, profiles, chats] = await Promise.all([
          await getVideo(Number(id)),
          await getLanguageCodes(),
          await getProfiles(),
          await getChats(),
        ]);

        setVideo(video);
        setLanguageCodes(languageCodes);
        setProfiles(profiles);
        setChats(chats);
      }
    } catch (error) {
      setError(error);
    }
  }, [id]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function validate() {
    const errors = validateVideo(video!);

    if (Object.keys(errors).length > 0) {
      setVideoErrors(errors as Record<keyof Video, string>);
    } else {
      setVideoErrors(undefined);
    }

    return Object.keys(errors).length === 0;
  }

  function handleVideoChange(video: Omit<Video, "id" | "createdAt" | "updatedAt">) {
    if (dirty) {
      validate();
    }

    setVideo(video);
  }

  async function handleVideoSave() {
    if (submitting) {
      return;
    }

    if (!validate()) {
      setDirty(true);
      return;
    }

    setDirty(false);

    enqueueSnackbar("Saving...", { variant: "info" });

    setSubmitting(true);

    try {
      if (id === "new") {
        const result = await createVideo(video!);
        enqueueSnackbar("Video created", { variant: "success" });
        navigate(`/content/new-videos/${result.id}`);
      } else {
        await updateVideo(Number(id), video!);
        enqueueSnackbar("Video saved", { variant: "success" });
        await fetchData();
      }
    } catch (e) {
      enqueueSnackbar("Failed to save video", { variant: "error" });
      console.error(e);
    }

    setSubmitting(false);
  }

  return (
    <>
      <Header />
      {loading && <div style={{ width: "50%" }}>
        <LinearProgress />
      </div>}
      {error && <div>Error: {error}</div>}
      {!loading && !error && <div className="social">
        <div className="box post-edit">
          <VideoForm
            video={video!}
            onVideoChange={handleVideoChange}
            languageCodes={languageCodes!}
            profiles={profiles!}
            chats={chats!}
            errors={videoErrors}
          />
        </div>
        <div className="links">
          <Link to="/content/new-videos">
            <Button
              disabled={submitting}
              variant="contained"
              color="primary"
              className="spacer"
            >
              Back
            </Button>
          </Link>
          <Button
            disabled={submitting}
            variant="contained"
            color="primary"
            onClick={handleVideoSave}
          >
            Save
          </Button>
        </div>
      </div>}
    </>
  );
};
